<template>
	<div class="dashboard-layout" :class="{ 'has-menu-strip': stripEnabled }">
		<!-- Touch screens: the menu folds into this strip; press, slide to an item and let go. -->
		<button
			v-if="stripEnabled"
			ref="menuStripRef"
			type="button"
			class="menu-strip"
			:class="{ 'is-open': stripOpen || stripTrackOffset !== null }"
			:aria-label="stripOpen ? 'Close menu' : 'Open menu'"
			:aria-expanded="stripOpen ? 'true' : 'false'"
			aria-controls="dashboard-menu"
			@pointerdown="onStripPointerDown"
			@pointermove="onStripPointerMove"
			@pointerup="onStripPointerUp"
			@pointercancel="onStripPointerCancel"
			@click="onStripClick"
		>
			<span class="menu-strip-grip" aria-hidden="true"></span>
		</button>
		<aside
			id="dashboard-menu"
			ref="menuPanelRef"
			class="dashboard-menu"
			:class="{ 'is-strip-open': stripEnabled && stripOpen, 'is-strip-tracking': stripTrackOffset !== null, 'is-strip-rising': stripRiseItems }"
			:style="stripPanelStyle"
			@click.capture="onPanelClickCapture"
			@pointerdown="onPanelPointerDown"
			@pointermove="onPanelPointerMove"
			@pointerup="onPanelPointerUp"
			@pointercancel="onPanelPointerUp"
		>
			<div class="menu-header">
				<img src="/logo-m.svg" alt="Logo" class="logo" />
				<button
					type="button"
					class="menu-back-btn"
					aria-label="Back"
					:disabled="!canGoBack"
					@click="handleBackTab"
				>
					<ChevronLeftIcon class="menu-back-icon" />
					Back
				</button>
			</div>

			<div class="menu-section">
				<nav v-if="menuReady" class="menu-nav menu-nav--enter" :class="{ 'is-entering': menuEntering }">
					<button
						v-for="item in journalMenuItems"
						:key="item.id"
						class="menu-item"
						:class="{ active: item.active }"
						@click="handleMenuClick(item)"
					>
						<component v-if="item.icon" :is="item.icon" class="item-icon" />
						<span class="menu-label">{{ item.label }}</span>
					</button>
					<p v-if="showMenuEmptyHints && !journalStore.loading && !journalMenuItems.length" class="menu-hint">No journals available</p>

					<div
						v-for="item in invoiceMenuItems"
						:key="item.id"
						class="menu-item-group"
						:class="{ 'has-subitem': activeInvoiceEditorTab && activeInvoiceEditorTab.listId === item.itemId }"
					>
						<button
							class="menu-item"
							:class="{ active: item.active }"
							:disabled="!clientInvoicesStore.listOptions.length"
							@click="handleMenuClick(item)"
						>
							<component
								v-if="item.icon"
								:is="activeInvoiceEditorTab && activeInvoiceEditorTab.listId === item.itemId && activeInvoiceEditorTab.isActive ? ChevronLeftIcon : item.icon"
								class="item-icon"
							/>
							<span class="menu-label">{{ item.label }}</span>
						</button>
						<Transition name="menu-subitem-wrap-transition">
							<div v-if="activeInvoiceEditorTab && activeInvoiceEditorTab.listId === item.itemId" class="menu-subitem-wrap">
								<button
									type="button"
									class="menu-item menu-subitem"
									:class="{ active: activeInvoiceEditorTab.isActive }"
									@click="tabsStore.activateTab(activeInvoiceEditorTab.id)"
								>
									<span class="menu-label">{{ activeInvoiceEditorTab.title }}</span>
								</button>
							</div>
						</Transition>
					</div>
					<p v-if="showMenuEmptyHints && !clientInvoicesStore.listsLoading && !invoiceMenuItems.length" class="menu-hint">No invoice lists configured</p>

					<div
						ref="documentsWrapperRef"
						class="menu-item-group documents-wrapper"
						:class="{ 'has-subitem': activeClientDocumentEditorTab }"
						@mouseenter="!stripEnabled && openDocumentsFlyout()"
						@mouseleave="!stripEnabled && handleDocumentsMouseLeave()"
						@focusout="handleDocumentsFocusOut"
					>
						<button
							ref="documentsParentRef"
							type="button"
							class="menu-item documents-parent"
							data-strip-submenu="documents"
							:class="{ active: documentsMenuItem.active, 'is-expanded': documentsExpanded }"
							aria-haspopup="menu"
							:aria-expanded="documentsExpanded ? 'true' : 'false'"
							aria-controls="documents-flyout"
							@click="handleDocumentsParentClick"
							@focus="handleDocumentsParentFocus"
							@keydown="handleDocumentsParentKeydown"
						>
							<component
								:is="activeClientDocumentEditorTab && activeClientDocumentEditorTab.isActive ? ChevronLeftIcon : documentsMenuItem.icon"
								class="item-icon"
							/>
							<span class="menu-label">{{ documentsMenuItem.label }}</span>
							<ChevronLeftIcon class="documents-chevron" />
						</button>
						<Transition name="menu-subitem-wrap-transition">
							<div v-if="activeClientDocumentEditorTab" class="menu-subitem-wrap">
								<button
									type="button"
									class="menu-item menu-subitem"
									:class="{ active: activeClientDocumentEditorTab.isActive }"
									@click="tabsStore.activateTab(activeClientDocumentEditorTab.id)"
								>
									<span class="menu-label">{{ activeClientDocumentEditorTab.title }}</span>
								</button>
							</div>
						</Transition>
						<nav
							v-if="documentsExpanded"
							id="documents-flyout"
							class="documents-flyout"
							:style="documentsFlyoutStyle"
							role="menu"
							aria-label="Document audiences"
						>
							<button
								v-for="(item, index) in documentAudienceMenuItems"
								:key="item.id"
								type="button"
								class="documents-flyout-item"
								:class="{ active: item.active }"
								role="menuitem"
								@click.stop="selectDocumentsAudience(item)"
								@keydown="handleDocumentsFlyoutKeydown($event, index)"
							>
								{{ item.label }}
							</button>
						</nav>
					</div>

					<button
						class="menu-item"
						:class="{ active: supplierInvoicesMenuItem.active }"
						@click="handleMenuClick(supplierInvoicesMenuItem)"
					>
						<component :is="supplierInvoicesMenuItem.icon" class="item-icon" />
						<span class="menu-label">{{ supplierInvoicesMenuItem.label }}</span>
					</button>

					<button
						class="menu-item"
						:class="{ active: analyticsMenuItem.active }"
						@click="handleMenuClick(analyticsMenuItem)"
					>
						<component :is="analyticsMenuItem.icon" class="item-icon" />
						<span class="menu-label">{{ analyticsMenuItem.label }}</span>
						<NotificationBadge
							:count="workPermissionsReportsStore.attentionCount"
							class="summary-due-count"
							singular-label="work permission follow-up requires attention"
							plural-label="work permission follow-ups require attention"
						/>
					</button>

				<div
					v-if="menuReady"
					ref="catalogWrapperRef"
					class="catalog-wrapper"
					@mouseenter="!stripEnabled && openCatalogFlyout()"
					@mouseleave="!stripEnabled && (catalogsExpanded = false)"
				>
					<button
						class="menu-item catalog-parent"
						:class="{ active: isCatalogActive, 'is-expanded': catalogsExpanded }"
						data-strip-submenu="catalogs"
						@click="handleCatalogParentClick"
					>
						<FolderIcon class="item-icon" />
						<span class="menu-label">Catalogs</span>
						<ChevronLeftIcon class="catalog-chevron" />
					</button>
					<nav v-if="catalogsExpanded" class="catalog-flyout" :style="catalogFlyoutStyle">
						<button
							v-for="item in catalogMenuItems"
							:key="item.id"
							class="catalog-flyout-item"
							:class="{ active: item.active }"
							@click="handleMenuClick(item)"
						>
							{{ item.label }}
						</button>
						<p v-if="!catalogMenuItems.length" class="menu-hint">No catalogs available</p>
					</nav>
				</div>
				</nav>
			</div>

			<div class="menu-footer">
				<div class="user-profile-section">
					<div class="user-actions">
						<button
							type="button"
							class="user-action-btn"
							:class="{ active: isSettingsActive }"
							title="Settings"
							@click="openSettings"
						>
							<SettingsIcon class="menu-icon" />
						</button>
						<button type="button" class="user-action-btn" title="Logout" @click="logout">
							<ExitIcon class="menu-icon" />
						</button>
					</div>
				</div>
			</div>
		</aside>
		<div
			v-if="stripEnabled"
			class="menu-strip-scrim"
			:class="{ 'is-visible': stripOpen, 'is-tracking': stripTrackOffset !== null }"
			:style="stripScrimStyle"
			aria-hidden="true"
			@click="closeStrip"
		></div>

		<section class="dashboard-content">
			<TabsBar
				v-if="displayTabsBar"
				:tabs="tabsStore.tabs"
				:active-tab-id="tabsStore.activeTabId"
				@activate="tabsStore.activateTab"
			/>
			<TabContent
				class="tab-content-area"
				:tabs="tabsStore.tabs"
				:active-tab-id="tabsStore.activeTabId"
			/>
		</section>
	</div>
</template>

<script setup>
import { computed, defineAsyncComponent, getCurrentInstance, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import {
	CalendarIcon,
	BasketIcon,
	InvoiceUpIcon,
	InvoiceDownIcon,
	DocumentIcon,
	ChartIcon,
	FolderIcon,
	ChevronLeftIcon,
	SettingsIcon,
	ExitIcon,
} from '@/components/icons/menuIcons';
import { useRouter } from 'vue-router';
import TabsBar from '@/components/TabsBar.vue';
import TabContent from '@/components/TabContent.vue';
import NotificationBadge from '@/components/NotificationBadge.vue';
import { useAuthStore } from '@/stores/auth';
import { useCatalogStore } from '@/stores/catalog';
import { useJournalStore } from '@/stores/journal';
import { useClientDocumentsStore } from '@/stores/clientDocuments';
import { useSupplierPortalAdminStore } from '@/stores/supplierPortalAdmin';
import { useProjectsStore } from '@/stores/projects';
import { useClientInvoicesStore } from '@/stores/clientInvoices';
import { useSupplierInvoicesStore } from '@/stores/supplierInvoices';
import { useTabsStore } from '@/stores/tabs';
import { useWorkPermissionsReportsStore } from '@/stores/workPermissionsReports';
import CatalogView from '@/views/catalog/CatalogView.vue';
import AppointmentsJournalView from '@/views/journal/AppointmentsJournalView.vue';
import PurchasesJournalView from '@/views/journal/PurchasesJournalView.vue';
import ClientDocumentEditorView from '@/views/client-documents/ClientDocumentEditorView.vue';
import DocumentsView from '@/views/documents/DocumentsView.vue';
import CompanyRecordEditorView from '@/views/company-records/CompanyRecordEditorView.vue';
import { formatRecordVersion } from '@/utils/companyRecords';
import {
	getClientDocumentDefinition,
	getClientDocumentLabel,
	getClientDocumentReference,
} from '@/utils/clientDocumentRegistry';
import { buildInvoiceDraftFromQuotation } from '@/utils/quotationToInvoice';
import {
	ASSIGNMENT_CONFIRMATION_DOCUMENT_TYPE,
	createAssignmentConfirmationTemplateContent,
} from '@/utils/assignmentConfirmationDocument';
import { applyAssignmentTaskTemplatesToNewContent } from '@/utils/assignmentSubtitlePresets';
import ClientInvoicesListView from '@/views/invoices/ClientInvoicesListView.vue';
import ClientInvoiceEditorView from '@/views/invoices/ClientInvoiceEditorView.vue';
import ReportsListView from '@/views/reports/ReportsListView.vue';
import ReportsEditorView from '@/views/reports/ReportsEditorView.vue';
import SettingsView from '@/views/settings/SettingsView.vue';
import SupplierInvoicesView from '@/views/payments/SupplierPaymentsView.vue';
import { settleWithin, whenFontsReady } from '@/utils/pageReveal';
import { useMenuStrip } from '@/composables/useMenuStrip';

// The map library is loaded only when someone opens the Live map.
const LiveMapView = defineAsyncComponent(() => import('@/views/map/LiveMapView.vue'));

const router = useRouter();
const tabsStore = useTabsStore();
const authStore = useAuthStore();
const catalogStore = useCatalogStore();
const journalStore = useJournalStore();
const clientDocumentsStore = useClientDocumentsStore();
const supplierPortalAdminStore = useSupplierPortalAdminStore();
const projectsStore = useProjectsStore();
const clientInvoicesStore = useClientInvoicesStore();
const supplierInvoicesStore = useSupplierInvoicesStore();
const workPermissionsReportsStore = useWorkPermissionsReportsStore();
const { proxy } = getCurrentInstance() || {};
const toast = proxy?.$toast;
const logError = proxy?.$logError;

const displayTabsBar = ref(false);
const showMenuEmptyHints = ref(false);
const catalogsExpanded = ref(false);
const catalogWrapperRef = ref(null);
const catalogFlyoutStyle = ref({});
const documentsExpanded = ref(false);
const documentsWrapperRef = ref(null);
const documentsParentRef = ref(null);
const documentsFlyoutStyle = ref({});
let suppressDocumentsFocusOpen = false;
let hintTimer = null;
const defaultTabOpened = ref(false);
const menuReady = ref(false);
const menuEntering = ref(false);
let menuEnterTimer = null;
const supplierTemplateCreating = ref(false);
const pinnedInvoiceEditorTabId = ref(null);
const pinnedInvoiceEditorListId = ref(null);
const pinnedClientDocumentEditorTabId = ref(null);
// Editors listed under Documents in the menu while they are open.
const DOCUMENT_EDITOR_SECTIONS = ['client-document-editor', 'company-record-editor'];
const DOCUMENT_AUDIENCES = ['clients', 'suppliers', 'records'];

const normalizeId = (value) => {
	if (value === null || value === undefined) return null;
	return String(value);
};

const normalizeDocumentDomain = (value) =>
	String(value || '').toLowerCase() === 'supplier' ? 'supplier' : 'client';

const callIfFunction = async (fn) => {
	if (typeof fn === 'function') {
		await fn();
	}
};

const prefetchClientInvoiceItems = async () => {
	const activeListId =
		normalizeId(clientInvoicesStore.activeListId) ||
		normalizeId(clientInvoicesStore.listOptions?.[0]?.id);
	if (!activeListId) return;

	const existingItems = clientInvoicesStore.itemsByListId?.[activeListId] || [];
	const isLoading = clientInvoicesStore.itemsLoadingByListId?.[activeListId];
	const hasPagination = Boolean(clientInvoicesStore.paginationByListId?.[activeListId]);
	if (existingItems.length || isLoading || hasPagination) {
		return;
	}

	try {
		await clientInvoicesStore.fetchItemsForList(activeListId, {
			filters: clientInvoicesStore.getActiveFilters(activeListId),
		});
	} catch (error) {
		console.warn('[dashboard] Prefetch client invoices failed', error);
	}
};

const prefetchSupplierInvoices = async () => {
	if (supplierInvoicesStore.hasFetched || supplierInvoicesStore.isLoading) return;
	try {
		await supplierInvoicesStore.fetchInvoices();
	} catch (error) {
		console.warn('[dashboard] Prefetch supplier invoices failed', error);
	}
};

const prefetchClientDocuments = async () => {
	if (clientDocumentsStore.hasFetched || clientDocumentsStore.isLoading) return;
	try {
		await clientDocumentsStore.fetchDocuments();
	} catch (error) {
		console.warn('[dashboard] Prefetch client documents failed', error);
	}
};

// The menu waits for the lists its items come from, so it appears once instead of growing item by item.
const MENU_WAIT_MS = 2500;
// Covers the staggered entrance, while the rising items still hang below the list.
const MENU_ENTER_MS = 900;

onMounted(() => {
	workPermissionsReportsStore.startLivePolling();

	// Do not block first paint on heavy catalog bootstrap.
	void callIfFunction(catalogStore.bootstrap).catch((error) => {
		console.warn('[dashboard] Catalog bootstrap failed', error);
	});
	const journalsLoaded = callIfFunction(journalStore.initialize).catch((error) => {
		console.warn('[dashboard] Journal init failed', error);
	});
	const invoiceListsLoaded = callIfFunction(clientInvoicesStore.initialize)
		.then(() => {
			void prefetchClientInvoiceItems();
		})
		.catch((error) => {
			console.warn('[dashboard] Client invoices init failed', error);
		});
	void settleWithin([journalsLoaded, invoiceListsLoaded, whenFontsReady()], MENU_WAIT_MS).then(() => {
		menuReady.value = true;
		menuEntering.value = true;
		menuEnterTimer = window.setTimeout(() => {
			menuEntering.value = false;
		}, MENU_ENTER_MS);
	});

	void prefetchSupplierInvoices();
	void prefetchClientDocuments();
	hintTimer = window.setTimeout(() => {
		showMenuEmptyHints.value = true;
	}, 3000);
});

onBeforeUnmount(() => {
	workPermissionsReportsStore.stopLivePolling();
	clearTimeout(menuEnterTimer);
	if (hintTimer) {
		clearTimeout(hintTimer);
		hintTimer = null;
	}
});

const activeTab = computed(
	() => tabsStore.tabs.find((tab) => tab.id === tabsStore.activeTabId) || null,
);
const canGoBack = computed(() => tabsStore.canGoBack);
const isSettingsActive = computed(() => activeTab.value?.component === SettingsView);

const activeMenuKey = computed(() => {
	const tab = activeTab.value;
	if (!tab) return null;
	if (tab.section === 'menu') {
		if (tab.component === CatalogView) {
			return tab.itemId ? `catalog:${tab.itemId}` : null;
		}
		if (tab.component === DocumentsView) {
			return 'documents';
		}
		if (tab.component === AppointmentsJournalView) {
			return tab.tabKey || 'journal:appointments';
		}
		if (tab.component === PurchasesJournalView) {
			return tab.tabKey || 'journal:purchases';
		}
		// The Live map opens from the Appointments page and belongs to it in the menu.
		if (tab.component === LiveMapView) {
			return 'journal:appointments';
		}
		if (tab.component === ClientInvoicesListView) {
			return tab.itemId ? `invoice:${tab.itemId}` : null;
		}
		if (tab.component === ReportsListView) {
			return 'analytics';
		}
		if (tab.component === ReportsEditorView) {
			return 'analytics';
		}
		if (tab.component === SupplierInvoicesView) {
			return 'supplier-invoices';
		}
	}
	if (tab.section === 'supplier-payment-editor') return 'supplier-invoices';
	if (tab.section === 'analytics-editor') {
		return 'analytics';
	}
	return null;
});

const activeInvoiceEditorTab = computed(() => {
	const tabId = pinnedInvoiceEditorTabId.value;
	const listId = pinnedInvoiceEditorListId.value;
	if (!tabId || !listId) return null;
	const tab = tabsStore.tabs.find((entry) => entry.id === tabId);
	if (!tab || tab.section !== 'invoice-editor') return null;
	if (normalizeId(tab.props?.listId) !== listId) return null;
	return {
		id: tab.id,
		listId,
		title: tab.title || 'Edit invoice',
		isActive: normalizeId(activeTab.value?.id) === normalizeId(tab.id),
	};
});

const setPinnedInvoiceEditor = (tab) => {
	if (!tab || tab.section !== 'invoice-editor') return;
	const listId = normalizeId(tab.props?.listId);
	if (!listId) return;
	pinnedInvoiceEditorTabId.value = tab.id;
	pinnedInvoiceEditorListId.value = listId;
};

const clearPinnedInvoiceEditor = () => {
	pinnedInvoiceEditorTabId.value = null;
	pinnedInvoiceEditorListId.value = null;
};

const activeClientDocumentEditorTab = computed(() => {
	const tabId = pinnedClientDocumentEditorTabId.value;
	if (!tabId) return null;
	const tab = tabsStore.tabs.find((entry) => entry.id === tabId);
	if (!tab || !DOCUMENT_EDITOR_SECTIONS.includes(tab.section)) return null;
	return {
		id: tab.id,
		title: tab.title || 'Edit proposal',
		isActive: normalizeId(activeTab.value?.id) === normalizeId(tab.id),
	};
});

const setPinnedClientDocumentEditor = (tab) => {
	if (!tab || !DOCUMENT_EDITOR_SECTIONS.includes(tab.section)) return;
	pinnedClientDocumentEditorTabId.value = tab.id;
};

const clearPinnedClientDocumentEditor = () => {
	pinnedClientDocumentEditorTabId.value = null;
};

const currentDocumentsAudience = computed(() => {
	const documentsTab = tabsStore.tabs.find(
		(tab) =>
			tab.component === DocumentsView &&
			tab.section === 'menu' &&
			(tab.tabKey === 'documents' || tab.itemId === 'documents'),
	);
	return DOCUMENT_AUDIENCES.includes(documentsTab?.props?.audience) ? documentsTab.props.audience : 'clients';
});

const documentsMenuItem = computed(() => ({
	id: 'documents',
	itemId: 'documents',
	tabKey: 'documents',
	label: 'Documents',
	active: activeMenuKey.value === 'documents',
	component: DocumentsView,
	props: { audience: currentDocumentsAudience.value },
	section: 'menu',
	listeners: createDocumentsViewListeners(),
	icon: DocumentIcon,
}));

const documentAudienceMenuItems = computed(() => [
	{
		...documentsMenuItem.value,
		id: 'documents:clients',
		label: 'Client documents',
		props: { audience: 'clients' },
		active: documentsMenuItem.value.active && currentDocumentsAudience.value === 'clients',
	},
	{
		...documentsMenuItem.value,
		id: 'documents:suppliers',
		label: 'Supplier documents',
		props: { audience: 'suppliers' },
		active: documentsMenuItem.value.active && currentDocumentsAudience.value === 'suppliers',
	},
	{
		...documentsMenuItem.value,
		id: 'documents:records',
		label: 'Company records',
		props: { audience: 'records' },
		active: documentsMenuItem.value.active && currentDocumentsAudience.value === 'records',
	},
]);

// A flyout opens 6px beside its parent. The strip menu's panel slides and the flyout travels with
// it (the panel is its containing block), so there it is placed relative to the panel.
const flyoutStyleFor = (wrapper) => {
	const rect = wrapper.getBoundingClientRect();
	const origin = stripEnabled.value && menuPanelRef.value
		? menuPanelRef.value.getBoundingClientRect()
		: { left: 0, top: 0 };
	return {
		position: 'fixed',
		top: `${rect.top - origin.top}px`,
		left: `${rect.right - origin.left + 6}px`,
	};
};

const openDocumentsFlyout = () => {
	documentsExpanded.value = true;
	if (documentsWrapperRef.value) {
		documentsFlyoutStyle.value = flyoutStyleFor(documentsWrapperRef.value);
	}
};

const closeDocumentsFlyout = () => {
	documentsExpanded.value = false;
};

const handleDocumentsParentFocus = () => {
	// In the strip menu a tap opens and closes the submenu; focus must not reopen it.
	if (stripEnabled.value) return;
	if (suppressDocumentsFocusOpen) {
		suppressDocumentsFocusOpen = false;
		return;
	}
	openDocumentsFlyout();
};

const handleDocumentsMouseLeave = () => {
	if (documentsWrapperRef.value?.contains(document.activeElement)) return;
	closeDocumentsFlyout();
};

const handleDocumentsFocusOut = (event) => {
	if (documentsWrapperRef.value?.contains(event.relatedTarget)) return;
	closeDocumentsFlyout();
};

const focusDocumentsFlyoutItem = async (index) => {
	openDocumentsFlyout();
	await nextTick();
	const items = documentsWrapperRef.value?.querySelectorAll('.documents-flyout-item') || [];
	if (!items.length) return;
	const normalizedIndex = (index + items.length) % items.length;
	items[normalizedIndex]?.focus();
};

const handleDocumentsParentKeydown = (event) => {
	if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
		event.preventDefault();
		void focusDocumentsFlyoutItem(0);
	} else if (event.key === 'Escape') {
		event.preventDefault();
		closeDocumentsFlyout();
	}
};

const handleDocumentsFlyoutKeydown = (event, index) => {
	if (event.key === 'ArrowDown') {
		event.preventDefault();
		void focusDocumentsFlyoutItem(index + 1);
	} else if (event.key === 'ArrowUp') {
		event.preventDefault();
		void focusDocumentsFlyoutItem(index - 1);
	} else if (event.key === 'Home') {
		event.preventDefault();
		void focusDocumentsFlyoutItem(0);
	} else if (event.key === 'End') {
		event.preventDefault();
		void focusDocumentsFlyoutItem(documentAudienceMenuItems.value.length - 1);
	} else if (event.key === 'Escape' || event.key === 'ArrowLeft') {
		event.preventDefault();
		closeDocumentsFlyout();
		suppressDocumentsFocusOpen = true;
		void nextTick(() => {
			documentsParentRef.value?.focus();
			suppressDocumentsFocusOpen = false;
		});
	}
};

const handleDocumentsParentClick = () => {
	if (stripEnabled.value) {
		if (documentsExpanded.value) closeDocumentsFlyout();
		else stripSubmenus.open('documents');
		return;
	}
	handleMenuClick(documentsMenuItem.value);
	closeDocumentsFlyout();
};

const selectDocumentsAudience = (item) => {
	handleMenuClick(item);
	closeDocumentsFlyout();
};

const catalogMenuItems = computed(() =>
	catalogStore.catalogList.map((catalog) => {
		const catalogId = normalizeId(catalog.id);
		const key = `catalog:${catalogId}`;
		return {
			id: key,
			itemId: catalogId,
			tabKey: 'catalogs',
			label: catalog.name || 'Untitled catalog',
			active: activeMenuKey.value === key,
			component: CatalogView,
			props: { catalogId },
			section: 'menu',
		};
	}),
);

const isCatalogActive = computed(() =>
	catalogMenuItems.value.some((item) => item.active),
);

const openCatalogFlyout = () => {
	catalogsExpanded.value = true;
	if (catalogWrapperRef.value) {
		catalogFlyoutStyle.value = flyoutStyleFor(catalogWrapperRef.value);
	}
};

// Without hover, a tap on Catalogs opens and closes its submenu.
const handleCatalogParentClick = () => {
	if (!stripEnabled.value) return;
	if (catalogsExpanded.value) catalogsExpanded.value = false;
	else stripSubmenus.open('catalogs');
};

const stripSubmenus = {
	open: (name) => {
		if (name === 'documents') {
			catalogsExpanded.value = false;
			openDocumentsFlyout();
		} else if (name === 'catalogs') {
			closeDocumentsFlyout();
			openCatalogFlyout();
		}
	},
	close: () => {
		closeDocumentsFlyout();
		catalogsExpanded.value = false;
	},
};

const menuPanelRef = ref(null);
const menuStripRef = ref(null);
const {
	enabled: stripEnabled,
	open: stripOpen,
	trackOffset: stripTrackOffset,
	riseItems: stripRiseItems,
	close: closeStrip,
	onStripPointerDown,
	onStripPointerMove,
	onStripPointerUp,
	onStripPointerCancel,
	onStripClick,
	onPanelPointerDown,
	onPanelPointerMove,
	onPanelPointerUp,
	onPanelClickCapture,
} = useMenuStrip({ panelRef: menuPanelRef, stripRef: menuStripRef, onClose: stripSubmenus.close });

// While a finger pulls the panel out or pushes it back, the panel and the dimming follow it.
const stripPanelStyle = computed(() =>
	stripTrackOffset.value === null ? undefined : { transform: `translateX(${stripTrackOffset.value}px)` },
);
const stripScrimStyle = computed(() => {
	if (stripTrackOffset.value === null) return undefined;
	const width = menuPanelRef.value?.offsetWidth || 220;
	return { opacity: String(Math.max(0, Math.min(1, 1 + stripTrackOffset.value / width))) };
});

const journalMenuItems = computed(() =>
	journalStore.journalList
		.map((journal) => {
			const normalized = (journal.name || '').trim().toLowerCase();
			let component = null;
			let tabKey = null;
			let key = null;
			if (normalized === 'appointments') {
				component = AppointmentsJournalView;
				tabKey = 'journal:appointments';
				key = tabKey;
			} else if (normalized === 'purchases') {
				component = PurchasesJournalView;
				tabKey = 'journal:purchases';
				key = tabKey;
			}
			if (!component || !tabKey) {
				return null;
			}
			return {
				id: tabKey,
				tabKey,
				label: journal.name || 'Untitled journal',
				active: activeMenuKey.value === tabKey,
				component,
				props: {},
				section: 'menu',
				listeners: normalized === 'appointments' ? { 'open-live-map': openLiveMap } : {},
				icon: normalized === 'appointments' ? CalendarIcon : BasketIcon,
			};
		})
		.filter(Boolean),
);

const invoiceListLabel = (list) => {
	const name = (list?.name || '').trim();
	if (!name) return 'Client Invoices';
	if (name.toLowerCase() === 'clients') return 'Client Invoices';
	return name;
};

const invoiceMenuLabel = (list) => {
	const name = (list?.name || '').trim();
	if (!name) return 'Client Invoices';
	if (name.toLowerCase() === 'clients') return 'Client Invoices';
	return name;
};

const invoiceMenuItems = computed(() =>
	clientInvoicesStore.listOptions.map((list) => {
		const listId = normalizeId(list.id);
		const key = `invoice:${listId}`;
		const tabTitle = invoiceListLabel(list);
		return {
			id: key,
			itemId: listId,
			tabKey: key,
			label: invoiceMenuLabel(list),
			tabTitle,
			active: activeMenuKey.value === key,
			component: ClientInvoicesListView,
			props: { listId, listTitle: tabTitle },
			section: 'menu',
			listeners: createInvoiceListListeners(listId),
			icon: InvoiceUpIcon,
		};
	}),
);

const analyticsMenuItem = computed(() => ({
	id: 'analytics',
	itemId: 'analytics',
	tabKey: 'analytics',
	label: 'Summary',
	active: activeMenuKey.value === 'analytics',
	component: ReportsListView,
	props: {},
	section: 'menu',
	listeners: createAnalyticsListListeners(),
	icon: ChartIcon,
}));

const supplierInvoicesMenuItem = computed(() => ({
	id: 'supplier-invoices',
	itemId: 'supplier-invoices',
	tabKey: 'supplier-invoices',
	label: 'Payments',
	active: activeMenuKey.value === 'supplier-invoices',
	component: SupplierInvoicesView,
	props: {},
	section: 'menu',
	icon: InvoiceDownIcon,
}));

const handleMenuClick = (item) => {
	if (!item?.component) return;
	if (item.component === ClientInvoicesListView) {
		clearPinnedInvoiceEditor();
	}
	if (item.component === DocumentsView) {
		clearPinnedClientDocumentEditor();
	}
	const existing = tabsStore.findMatchingTab({
		component: item.component,
		itemId: item.itemId,
		section: 'menu',
		tabKey: item.tabKey || item.id,
	});
	if (existing) {
		tabsStore.updateTab(existing.id, {
			props: item.props || {},
			title: item.tabTitle || item.label,
			itemId: item.itemId,
			icon: item.icon,
			section: 'menu',
			tabKey: item.tabKey || item.id,
			listeners: item.listeners || {},
		});
		tabsStore.activateTab(existing.id);
		return;
	}
	const tab = tabsStore.createTab({
		component: item.component,
		title: item.tabTitle || item.label,
		props: item.props || {},
		icon: item.icon,
		itemId: item.itemId,
		section: 'menu',
		listeners: item.listeners || {},
		tabKey: item.tabKey || item.id,
	});
	if (tab) {
		tabsStore.activateTab(tab.id);
	}
};

// Today's jobs on a map of London, opened from the Appointments page.
const liveMapTab = {
	id: 'live-map',
	itemId: 'live-map',
	tabKey: 'live-map',
	label: 'Live map',
	component: LiveMapView,
	props: {},
	section: 'menu',
	icon: CalendarIcon,
};
const openLiveMap = () => handleMenuClick(liveMapTab);

const handleBackTab = () => {
	const targetTabId = tabsStore.goBackTab();
	if (!targetTabId) return;
	const targetTab = tabsStore.tabs.find((tab) => tab.id === targetTabId);
	if (targetTab?.component === ClientInvoicesListView) {
		clearPinnedInvoiceEditor();
	}
	if (targetTab?.component === DocumentsView) {
		clearPinnedClientDocumentEditor();
	}
};

watch(
	() => ({
		authenticated: authStore.isAuthenticated,
		tabCount: tabsStore.tabs.length,
		journalCount: journalMenuItems.value.length,
	}),
	(state) => {
		if (defaultTabOpened.value) return;
		if (!state.authenticated) return;
		if (state.tabCount > 0) return;
		const appointmentsItem = journalMenuItems.value.find(
			(item) => (item.tabKey || item.id) === 'journal:appointments',
		);
		if (appointmentsItem) {
			defaultTabOpened.value = true;
			handleMenuClick(appointmentsItem);
		}
	},
	{ immediate: true },
);

watch(
	() => activeTab.value,
	(tab) => {
		if (tab?.section === 'invoice-editor') {
			setPinnedInvoiceEditor(tab);
		}
		if (DOCUMENT_EDITOR_SECTIONS.includes(tab?.section)) {
			setPinnedClientDocumentEditor(tab);
		}
	},
	{ immediate: true },
);

watch(
	() => tabsStore.tabs.map((tab) => tab.id),
	(tabIds) => {
		if (pinnedInvoiceEditorTabId.value && !tabIds.includes(pinnedInvoiceEditorTabId.value)) {
			clearPinnedInvoiceEditor();
		}
		if (pinnedClientDocumentEditorTabId.value && !tabIds.includes(pinnedClientDocumentEditorTabId.value)) {
			clearPinnedClientDocumentEditor();
		}
	},
);

const logout = () => {
	authStore.logout();
	router.push({ name: 'auth' });
};

const openSettings = () => {
	const existing = tabsStore.findMatchingTab({
		component: SettingsView,
		section: 'settings',
		tabKey: 'settings',
	});
	if (existing) {
		tabsStore.activateTab(existing.id);
		return;
	}
	const tab = tabsStore.createTab({
		component: SettingsView,
		title: 'Settings',
		itemId: 'settings',
		section: 'settings',
		tabKey: 'settings',
	});
	if (tab) {
		tabsStore.activateTab(tab.id);
	}
};

function createInvoiceListListeners(listId) {
	const normalizedListId = normalizeId(listId);
	return {
		'open-editor': (payload) =>
			handleOpenInvoiceEditor({
				listId: normalizedListId,
				draftId: payload?.draftId,
				mode: payload?.mode,
				sourceDocumentId: payload?.sourceDocumentId,
				sourceInvoiceNumber: payload?.sourceInvoiceNumber,
			}),
		'open-invoice': (payload) =>
			handleOpenExistingInvoice({
				listId: normalizedListId,
				documentId: payload?.documentId,
				invoice: payload?.invoice,
			}),
	};
}

function createAnalyticsListListeners() {
	return {
		'open-editor': (payload) =>
			handleOpenAnalyticsEditor({
				reportId: payload?.reportId || null,
				reportType: payload?.reportType || 'analytics',
			}),
	};
}

function createDocumentsViewListeners() {
	return {
		'open-document': (payload) => handleOpenDocumentEditor(payload),
		'convert-to-invoice': (payload) => handleConvertQuotationToInvoice(payload),
		'open-invoice': (payload) => handleOpenExistingInvoice(payload),
		'create-template': (payload) => handleCreateDocumentTemplate(payload),
		'document-transferred': (payload) => handleClientDocumentTransferred(payload),
		'open-record': (payload) => handleOpenCompanyRecord(payload),
		'record-deleted': (payload) => handleCompanyRecordDeleted(payload),
	};
}

const catalogItemsByName = (name) => {
	const target = String(name || '').trim().toLowerCase();
	const catalog = Object.values(catalogStore.catalogsById || {}).find(
		(entry) => String(entry?.name || '').trim().toLowerCase() === target,
	);
	return catalog?.id ? catalogStore.itemsByCatalogId?.[catalog.id] || [] : [];
};

const findInvoiceConvertedFromQuotation = (quotationId) => {
	const normalizedQuotationId = normalizeId(quotationId);
	if (!normalizedQuotationId) return null;
	const indexedInvoice = clientInvoicesStore.quotationInvoiceLinks?.[normalizedQuotationId];
	if (indexedInvoice) return indexedInvoice;
	const cachedInvoices = Object.values(clientInvoicesStore.currentInvoiceCache || {});
	const listedInvoices = Object.values(clientInvoicesStore.itemsByListId || {}).flatMap(
		(items) => (Array.isArray(items) ? items : []),
	);
	return [...cachedInvoices, ...listedInvoices].find(
		(invoice) =>
			normalizeId(invoice?.metadata?.sourceQuotation?.documentId) ===
			normalizedQuotationId,
	) || null;
};

async function handleConvertQuotationToInvoice(payload = {}) {
	const quotationId = normalizeId(
		payload?.quotationId || payload?.quotation?.documentId || payload?.quotation?.id,
	);
	try {
		const existingSourceTab = quotationId
			? tabsStore.tabs.find(
					(tab) =>
						tab.section === 'invoice-editor' &&
						normalizeId(tab.props?.sourceQuotationDocumentId) === quotationId,
				)
			: null;
		if (existingSourceTab) {
			setPinnedInvoiceEditor(existingSourceTab);
			tabsStore.activateTab(existingSourceTab.id);
			return;
		}

		await clientInvoicesStore.fetchQuotationInvoiceLinks({ force: true });
		const listId =
			normalizeId(clientInvoicesStore.activeListId) ||
			normalizeId(clientInvoicesStore.listOptions?.[0]?.id);
		if (!listId) {
			throw new Error('No client invoice list is configured.');
		}
		const existingInvoice = findInvoiceConvertedFromQuotation(quotationId);
		const existingInvoiceId = normalizeId(
			existingInvoice?.documentId || existingInvoice?.id,
		);
		if (existingInvoice && existingInvoiceId) {
			handleOpenExistingInvoice({
				listId: normalizeId(existingInvoice.listId) || listId,
				documentId: existingInvoiceId,
				invoice: existingInvoice,
			});
			toast?.info?.(
				`Invoice ${existingInvoice.invoiceNumber || existingInvoiceId} was already created from this quotation.`,
			);
			return;
		}

		const quotation = quotationId
			? await clientDocumentsStore.fetchDocument(quotationId, { forceRefresh: true })
			: payload?.quotation;
		if (!quotation) {
			throw new Error('Quotation not found.');
		}

		try {
			await catalogStore.bootstrap?.();
		} catch (catalogError) {
			logError?.(catalogError, 'DashboardView quotation conversion catalog bootstrap');
		}
		const clientOptions = catalogItemsByName('Clients');
		if (!clientOptions.length) {
			throw new Error('The client catalog is unavailable. Reload the page and try again.');
		}

		const { invoice, warnings } = buildInvoiceDraftFromQuotation(quotation, {
			clientOptions,
			buildingOptions: catalogItemsByName("Clients’ Buildings"),
			serviceOptions: catalogItemsByName('Services'),
		});
		handleOpenInvoiceEditor({
			listId,
			draftId: quotationId ? `quotation-${quotationId}` : undefined,
			sourceQuotationDocumentId: quotationId,
			initialInvoice: invoice,
		});

		if (warnings.length) {
			const notify = toast?.warn || toast?.info;
			notify?.call(toast, warnings.join(' '));
		}
	} catch (error) {
		logError?.(error, 'DashboardView handleConvertQuotationToInvoice');
		toast?.error?.(error?.message || 'Failed to prepare an invoice from this quotation.');
	}
}

function handleClientDocumentTransferred(payload = {}) {
	const sourceDocumentId = normalizeId(payload?.sourceDocumentId);
	if (!sourceDocumentId) return;
	const sourceItemId = `client:${sourceDocumentId}`;
	const staleEditorTabs = tabsStore.tabs.filter(
		(tab) =>
			tab.section === 'client-document-editor' &&
			normalizeId(tab.itemId) === sourceItemId,
	);
	staleEditorTabs.forEach((tab) => tabsStore.closeTab(tab.id));
	if (
		staleEditorTabs.some(
			(tab) => normalizeId(tab.id) === normalizeId(pinnedClientDocumentEditorTabId.value),
		)
	) {
		clearPinnedClientDocumentEditor();
	}
}

function createInvoiceEditorListeners(listId) {
	const normalizedListId = normalizeId(listId);
	return {
		'back-to-list': (payload) =>
			handleBackToList({
				listId: normalizedListId,
				tabId: payload?.tabId,
			}),
	};
}

function createClientDocumentEditorListeners(documentDomain = 'client') {
	const audience = documentDomain === 'supplier' ? 'suppliers' : 'clients';
	return {
		'back-to-list': (payload) =>
			handleBackToDocuments({
				tabId: payload?.tabId,
				audience,
			}),
	};
}

function openDocumentsTab({ audience = 'clients' } = {}) {
	const normalizedAudience = DOCUMENT_AUDIENCES.includes(audience) ? audience : 'clients';
	const existing = tabsStore.findMatchingTab({
		component: DocumentsView,
		itemId: 'documents',
		section: 'menu',
		tabKey: 'documents',
	});
	if (existing) {
		tabsStore.updateTab(existing.id, {
			title: 'Documents',
			props: { audience: normalizedAudience },
			listeners: createDocumentsViewListeners(),
		});
		tabsStore.activateTab(existing.id);
		return existing;
	}
	const tab = tabsStore.createTab({
		component: DocumentsView,
		title: 'Documents',
		props: { audience: normalizedAudience },
		itemId: 'documents',
		section: 'menu',
		listeners: createDocumentsViewListeners(),
		tabKey: 'documents',
		icon: DocumentIcon,
	});
	if (tab) {
		tabsStore.activateTab(tab.id);
	}
	return tab;
}

function handleBackToDocuments({ tabId, audience = 'clients' } = {}) {
	openDocumentsTab({ audience });
	if (tabId) {
		tabsStore.closeTab(tabId);
	}
	clearPinnedClientDocumentEditor();
}

function openInvoiceListTab(rawListId) {
	const listId = normalizeId(rawListId);
	if (!listId) return null;

	const existing = tabsStore.findMatchingTab({
		component: ClientInvoicesListView,
		itemId: listId,
		section: 'menu',
		tabKey: `invoice:${listId}`,
	});
	if (existing) {
		tabsStore.activateTab(existing.id);
		return existing;
	}

	const list = clientInvoicesStore.listOptions.find(
		(entry) => normalizeId(entry.id) === listId,
	);
	const title = invoiceListLabel(list);
	const tab = tabsStore.createTab({
		component: ClientInvoicesListView,
		title,
		props: { listId, listTitle: title },
		itemId: listId,
		section: 'menu',
		listeners: createInvoiceListListeners(listId),
		tabKey: `invoice:${listId}`,
	});
	if (tab) {
		tabsStore.activateTab(tab.id);
	}
	return tab;
}

function handleBackToList({ listId, tabId } = {}) {
	const normalizedListId = normalizeId(listId) || normalizeId(clientInvoicesStore.activeListId);
	if (normalizedListId) {
		openInvoiceListTab(normalizedListId);
	}
	if (tabId) {
		tabsStore.closeTab(tabId);
	}
}

function handleOpenInvoiceEditor({
	listId,
	draftId,
	mode = 'invoice',
	sourceDocumentId,
	sourceInvoiceNumber,
	sourceQuotationDocumentId,
	initialInvoice = null,
} = {}) {
	const normalizedListId =
		normalizeId(listId) || normalizeId(clientInvoicesStore.activeListId);
	if (!normalizedListId) return;
	const normalizedMode = mode === 'credit-note' ? 'credit-note' : 'invoice';
	const normalizedSourceDocumentId = normalizeId(sourceDocumentId);
	const fallbackDraftId =
		normalizedMode === 'credit-note' && normalizedSourceDocumentId
			? `cn-${normalizedSourceDocumentId}`
			: Math.random().toString(36).slice(2, 10);
	const newDraftId = draftId ? normalizeId(draftId) : fallbackDraftId;
	const existing = tabsStore.findMatchingTab({
		component: ClientInvoiceEditorView,
		itemId: newDraftId,
		section: 'invoice-editor',
	});
	if (existing) {
		setPinnedInvoiceEditor(existing);
		tabsStore.activateTab(existing.id);
		return;
	}
	const tab = tabsStore.createTab({
		component: ClientInvoiceEditorView,
		title: normalizedMode === 'credit-note' ? 'New credit note' : 'New client invoice',
		props: {
			listId: normalizedListId,
			draftId: newDraftId,
			mode: normalizedMode,
			sourceDocumentId: normalizedSourceDocumentId,
			sourceInvoiceNumber: sourceInvoiceNumber || null,
			sourceQuotationDocumentId: normalizeId(sourceQuotationDocumentId),
			initialInvoice,
		},
		itemId: newDraftId,
		section: 'invoice-editor',
		listeners: createInvoiceEditorListeners(normalizedListId),
	});
	if (tab) {
		tabsStore.updateTab(tab.id, {
			props: {
				...(tab.props || {}),
				tabId: tab.id,
			},
		});
		setPinnedInvoiceEditor(tab);
		tabsStore.activateTab(tab.id);
	}
}

function handleOpenExistingInvoice({ listId, documentId, invoice } = {}) {
	const normalizedListId =
		normalizeId(listId) || normalizeId(clientInvoicesStore.activeListId);
	const normalizedDocumentId = normalizeId(documentId);
	if (!normalizedListId || !normalizedDocumentId) return;

	const existing = tabsStore.findMatchingTab({
		component: ClientInvoiceEditorView,
		itemId: normalizedDocumentId,
		section: 'invoice-editor',
	});
	if (existing) {
		setPinnedInvoiceEditor(existing);
		tabsStore.activateTab(existing.id);
		return;
	}

	const initialTitle = invoice?.invoiceNumber
		? `Edit invoice #${invoice.invoiceNumber}`
		: `Edit invoice #${normalizedDocumentId}`;
	const tab = tabsStore.createTab({
		component: ClientInvoiceEditorView,
		title: initialTitle,
		props: {
			listId: normalizedListId,
			documentId: normalizedDocumentId,
			invoiceNumber: invoice?.invoiceNumber || null,
		},
		itemId: normalizedDocumentId,
		section: 'invoice-editor',
		listeners: createInvoiceEditorListeners(normalizedListId),
	});
	if (tab) {
		tabsStore.updateTab(tab.id, {
			props: {
				...(tab.props || {}),
				tabId: tab.id,
			},
		});
		setPinnedInvoiceEditor(tab);
		tabsStore.activateTab(tab.id);
	}
}

function handleOpenClientDocumentEditor(payload = {}) {
	return handleOpenDocumentEditor({
		...payload,
		domain: 'client',
	});
}

async function handleCreateDocumentTemplate(payload = {}) {
	const domain = normalizeDocumentDomain(payload?.documentDomain || payload?.domain);
	if (domain !== 'supplier' || payload?.documentId || payload?.draftId || payload?.document?.id) {
		return handleOpenDocumentEditor(payload);
	}

	const definition = getClientDocumentDefinition(ASSIGNMENT_CONFIRMATION_DOCUMENT_TYPE);
	const templateKey = String(payload?.templateKey || '').trim();
	if (templateKey !== definition.template) {
		toast?.error?.('This supplier document template is not supported yet.');
		return null;
	}
	if (supplierTemplateCreating.value) return null;

	supplierTemplateCreating.value = true;
	try {
		if (!projectsStore.initialized) {
			await projectsStore.ensureInitialized();
		}
		const document = await supplierPortalAdminStore.createDocument({
			title: definition.label,
			category: payload?.category || 'assignments',
			sourceType: 'template',
			templateKey,
			content: applyAssignmentTaskTemplatesToNewContent(
				createAssignmentConfirmationTemplateContent(),
				projectsStore.activeProject?.documentPresets,
			),
			metadata: {
				template: definition.template,
				sourceTemplate: definition.sourceTemplate,
			},
			completionMode: 'bilateral_signature',
			requiresEffectiveDate: true,
			supplierIds: [],
		});
		if (!document?.id) {
			throw new Error('Supplier document draft was not created');
		}
		handleOpenDocumentEditor({
			domain: 'supplier',
			documentId: document.id,
			document,
		});
		return document;
	} catch (error) {
		logError?.(error, 'DashboardView handleCreateDocumentTemplate');
		toast?.error?.(
			supplierPortalAdminStore.error || error?.message || 'Failed to create supplier document',
		);
		return null;
	} finally {
		supplierTemplateCreating.value = false;
	}
}

function handleCompanyRecordDeleted({ documentId } = {}) {
	const itemId = `record:${normalizeId(documentId)}`;
	tabsStore.tabs
		.filter((tab) => tab.section === 'company-record-editor' && normalizeId(tab.itemId) === itemId)
		.forEach((tab) => tabsStore.closeTab(tab.id));
}

function createCompanyRecordEditorListeners() {
	return {
		'open-record': (payload) => handleOpenCompanyRecord(payload),
	};
}

// Each version of a company record opens in its own tab; starting the next review swaps the tab.
function handleOpenCompanyRecord({ record, replaceTabId = null } = {}) {
	const documentId = normalizeId(record?.documentId);
	if (!documentId) return;
	const itemId = `record:${documentId}`;
	const existing = tabsStore.findMatchingTab({
		component: CompanyRecordEditorView,
		itemId,
		section: 'company-record-editor',
	});
	const tab = existing || tabsStore.createTab({
		component: CompanyRecordEditorView,
		title: `${record.title || 'Company record'} ${formatRecordVersion(record.version)}`,
		props: { documentId },
		itemId,
		section: 'company-record-editor',
		listeners: createCompanyRecordEditorListeners(),
	});
	if (!tab) return;
	if (!existing) tabsStore.updateTab(tab.id, { props: { documentId, tabId: tab.id } });
	setPinnedClientDocumentEditor(tab);
	tabsStore.activateTab(tab.id);
	if (replaceTabId && normalizeId(replaceTabId) !== normalizeId(tab.id)) {
		tabsStore.closeTab(replaceTabId);
	}
}

function handleOpenDocumentEditor({
	domain,
	documentDomain,
	documentId,
	draftId,
	documentType,
	initialContent,
	document,
	templateType,
	templateKey,
	title,
} = {}) {
	const normalizedDomain = normalizeDocumentDomain(documentDomain || domain);
	const normalizedDocumentId = normalizeId(documentId || document?.documentId || document?.id);
	const normalizedDraftId = normalizeId(draftId);
	const requestedDocumentType =
		document?.documentType ||
		document?.content?.documentType ||
		initialContent?.documentType ||
		documentType ||
		templateType ||
		null;
	const editorDocumentId = normalizedDocumentId || normalizedDraftId;
	if (!editorDocumentId) return;
	const editorItemId = `${normalizedDomain}:${editorDocumentId}`;

	const existing = tabsStore.findMatchingTab({
		component: ClientDocumentEditorView,
		itemId: editorItemId,
		section: 'client-document-editor',
	});
	if (existing) {
		tabsStore.updateTab(existing.id, {
			props: {
				documentId: normalizedDocumentId,
				draftId: normalizedDraftId,
				documentType: requestedDocumentType,
				initialContent: initialContent || null,
				documentDomain: normalizedDomain,
			},
			listeners: createClientDocumentEditorListeners(normalizedDomain),
		});
		setPinnedClientDocumentEditor(existing);
		tabsStore.activateTab(existing.id);
		return;
	}

	const label = normalizedDomain === 'supplier'
		? (
			title ||
			document?.title ||
			(templateType === 'assignment-confirmation' || templateKey === 'assignment-confirmation-v1'
				? 'Assignment confirmation'
				: 'Supplier document')
		)
		: getClientDocumentLabel(
				document || requestedDocumentType || initialContent || {},
			);
	const canResolveTemplateReference =
		normalizedDomain === 'client' ||
		document?.documentType === 'assignment-confirmation' ||
		document?.content?.documentType === 'assignment-confirmation';
	const reference = document
		? (canResolveTemplateReference ? getClientDocumentReference(document) : document.reference || '')
		: '';
	const initialTitle = reference
		? `${label} ${reference}`
		: label;
	const tab = tabsStore.createTab({
		component: ClientDocumentEditorView,
		title: initialTitle,
		props: {
			documentId: normalizedDocumentId,
			draftId: normalizedDraftId,
			documentType: requestedDocumentType,
			initialContent: initialContent || null,
			documentDomain: normalizedDomain,
		},
		itemId: editorItemId,
		section: 'client-document-editor',
		listeners: createClientDocumentEditorListeners(normalizedDomain),
	});
	if (tab) {
		tabsStore.updateTab(tab.id, {
			props: {
				...(tab.props || {}),
				tabId: tab.id,
			},
		});
		setPinnedClientDocumentEditor(tab);
		tabsStore.activateTab(tab.id);
	}
}

function handleOpenAnalyticsEditor({ reportId, reportType = 'analytics' } = {}) {
	const normalizedReportType = reportType === 'work-permissions'
		? 'work-permissions'
		: 'analytics';
	const normalizedReportId = normalizeId(reportId);
	const draftId = normalizedReportId || Math.random().toString(36).slice(2, 10);
	const editorItemId = `${normalizedReportType}:${draftId}`;
	const existing = tabsStore.findMatchingTab({
		component: ReportsEditorView,
		itemId: editorItemId,
		section: 'analytics-editor',
	});
	if (existing) {
		tabsStore.activateTab(existing.id);
		return;
	}
	const tab = tabsStore.createTab({
		component: ReportsEditorView,
		title: normalizedReportType === 'work-permissions'
			? 'Work permissions report'
			: (normalizedReportId ? 'Analytics report' : 'New analytics report'),
		props: {
			reportId: normalizedReportId,
			reportType: normalizedReportType,
			draftId,
		},
		itemId: editorItemId,
		section: 'analytics-editor',
	});
	if (tab) {
		tabsStore.updateTab(tab.id, {
			props: {
				...(tab.props || {}),
				tabId: tab.id,
			},
		});
		tabsStore.activateTab(tab.id);
	}
}
</script>

<style scoped>
.dashboard-layout {
	height: 100vh;
	height: 100dvh;
	overflow: hidden;
	display: flex;
	background: var(--noc-background);
	min-width: 0;
}

.dashboard-menu {
	position: relative;
	width: 220px;
	min-width: 220px;
	flex: 0 0 220px;
	background: var(--noc-sidebar-background);
	border-right: 1px solid var(--border-color-secondary);
	display: flex;
	flex-direction: column;
	padding: 18px;
}

.menu-header,
.menu-section {
	position: relative;
	z-index: 1;
}

/* Touch screens: the sidebar folds into a thin strip (useMenuStrip). A tap on it, or a swipe
   from the left edge, brings the menu out; a swiping finger pulls the panel along. */
.menu-strip {
	position: relative;
	z-index: 30;
	display: flex;
	align-items: center;
	justify-content: center;
	flex: 0 0 12px;
	width: 12px;
	padding: 0;
	border: 0;
	border-right: 1px solid var(--border-color-secondary);
	background: var(--noc-sidebar-background);
	cursor: pointer;
	touch-action: none;
	user-select: none;
	-webkit-user-select: none;
	-webkit-touch-callout: none;
	-webkit-tap-highlight-color: transparent;
}

/* A wider invisible grip, over the page's own padding. */
.menu-strip::before {
	content: '';
	position: absolute;
	inset: 0 -14px 0 0;
}

.menu-strip-grip {
	width: 4px;
	height: 40px;
	border-radius: 2px;
	background: color-mix(in srgb, var(--text-color-secondary) 32%, transparent);
	transition:
		opacity 200ms ease,
		transform 260ms cubic-bezier(0.22, 1, 0.36, 1);
}

.menu-strip.is-open .menu-strip-grip {
	opacity: 0;
	transform: scaleY(0.5);
}

.menu-strip:focus-visible {
	outline: none;
}

.menu-strip:focus-visible .menu-strip-grip {
	background: var(--noc-blue);
}

.has-menu-strip .dashboard-menu {
	position: fixed;
	top: 0;
	bottom: 0;
	left: 0;
	z-index: 60;
	width: 220px;
	min-width: 0;
	flex: none;
	visibility: hidden;
	transform: translateX(-100%);
	touch-action: pan-y;
	user-select: none;
	-webkit-user-select: none;
	-webkit-touch-callout: none;
	-webkit-tap-highlight-color: transparent;
	/* Folding away is quicker than opening, and starts gently. */
	transition:
		transform 200ms cubic-bezier(0.4, 0, 1, 1),
		box-shadow 200ms ease,
		visibility 0s linear 200ms;
}

/* While it slides away, a finger already reaching for the strip goes through to it. */
.has-menu-strip .dashboard-menu:not(.is-strip-open, .is-strip-tracking) {
	pointer-events: none;
}

.has-menu-strip .dashboard-menu.is-strip-open {
	visibility: visible;
	transform: translateX(0);
	box-shadow: 16px 0 40px rgba(15, 18, 25, 0.14);
	transition:
		transform 280ms cubic-bezier(0.22, 1, 0.36, 1),
		box-shadow 280ms ease,
		visibility 0s;
}

/* The panel follows a finger that pulls it out or pushes it back. */
.has-menu-strip .dashboard-menu.is-strip-tracking {
	visibility: visible;
	box-shadow: 16px 0 40px rgba(15, 18, 25, 0.14);
	transition: none;
}

.menu-strip-scrim {
	position: fixed;
	inset: 0;
	z-index: 55;
	background: rgba(15, 18, 25, 0.16);
	opacity: 0;
	pointer-events: none;
	transition: opacity 200ms ease;
}

.menu-strip-scrim.is-visible {
	opacity: 1;
	pointer-events: auto;
	transition-duration: 260ms;
}

/* While a finger moves the panel, the dimming follows it. */
.menu-strip-scrim.is-tracking {
	transition: none;
}

/* When a tap opens the menu, the items follow the panel in, a step apart. */
.has-menu-strip .is-strip-open.is-strip-rising .menu-nav > * {
	animation: menu-strip-item-enter 320ms cubic-bezier(0.22, 1, 0.36, 1) backwards;
}

.has-menu-strip .is-strip-open.is-strip-rising .menu-nav > :nth-child(2) { animation-delay: 15ms; }
.has-menu-strip .is-strip-open.is-strip-rising .menu-nav > :nth-child(3) { animation-delay: 30ms; }
.has-menu-strip .is-strip-open.is-strip-rising .menu-nav > :nth-child(4) { animation-delay: 45ms; }
.has-menu-strip .is-strip-open.is-strip-rising .menu-nav > :nth-child(5) { animation-delay: 60ms; }
.has-menu-strip .is-strip-open.is-strip-rising .menu-nav > :nth-child(6) { animation-delay: 75ms; }
.has-menu-strip .is-strip-open.is-strip-rising .menu-nav > :nth-child(7) { animation-delay: 90ms; }
.has-menu-strip .is-strip-open.is-strip-rising .menu-nav > :nth-child(n + 8) { animation-delay: 105ms; }

@keyframes menu-strip-item-enter {
	from {
		opacity: 0;
		transform: translateX(-10px);
	}
}

@media (prefers-reduced-motion: reduce) {
	.has-menu-strip .dashboard-menu,
	.has-menu-strip .dashboard-menu.is-strip-open,
	.menu-strip-scrim,
	.menu-strip-grip {
		transition: none;
	}

	.has-menu-strip .is-strip-open.is-strip-rising .menu-nav > * {
		animation: none;
	}
}

.menu-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 4px;
}

.logo {
	width: 32px;
	height: 32px;
	border-radius: 4px;
	object-fit: contain;
}

.menu-section {
	display: flex;
	flex-direction: column;
	flex: 1 1 auto;
	min-height: 0;
}

.menu-nav {
	display: flex;
	flex-direction: column;
	gap: 4px;
	margin-top: 6px;
	overflow-y: auto;
	min-height: 0;
}

/* Once its lists are in, the menu rises into place item by item from the top. */
.menu-nav--enter > * {
	animation: menu-item-enter 560ms cubic-bezier(0.16, 1, 0.3, 1) backwards;
}

.menu-nav--enter > :nth-child(2) { animation-delay: 40ms; }
.menu-nav--enter > :nth-child(3) { animation-delay: 80ms; }
.menu-nav--enter > :nth-child(4) { animation-delay: 120ms; }
.menu-nav--enter > :nth-child(5) { animation-delay: 160ms; }
.menu-nav--enter > :nth-child(6) { animation-delay: 200ms; }
.menu-nav--enter > :nth-child(7) { animation-delay: 240ms; }
.menu-nav--enter > :nth-child(n + 8) { animation-delay: 280ms; }

/* The rising items briefly hang below the list; without this it would flash a scrollbar. */
.menu-nav.is-entering {
	overflow-y: hidden;
}

@keyframes menu-item-enter {
	from {
		opacity: 0;
		transform: translateY(8px);
	}
}

/* The logo and the footer buttons fade in with the frame instead of popping in. */
.menu-header,
.menu-footer {
	animation: menu-chrome-enter 420ms ease-out backwards;
}

@keyframes menu-chrome-enter {
	from {
		opacity: 0;
	}
}

@media (prefers-reduced-motion: reduce) {
	.menu-nav--enter > *,
	.menu-header,
	.menu-footer {
		animation: none;
	}
}

.catalog-wrapper,
.documents-wrapper {
	position: relative;
}

.catalog-parent,
.documents-parent {
	justify-content: flex-start;
}

.catalog-chevron,
.documents-chevron {
	font-size: 0.8rem;
	width: 0.8rem;
	height: 0.8rem;
	margin-left: auto;
	opacity: 0.45;
	transform: rotate(180deg);
}

.catalog-flyout,
.documents-flyout {
	min-width: 180px;
	background: var(--noc-background);
	border: 1px solid var(--border-color);
	border-radius: 4px;
	box-shadow: 0 8px 24px rgba(0, 0, 0, 0.10), 0 2px 8px rgba(0, 0, 0, 0.06);
	padding: 6px;
	z-index: 100;
	display: flex;
	flex-direction: column;
	gap: 2px;
	animation: catalog-flyout-in 0.14s ease-out;
}

/* Invisible bridge so the mouse can travel from parent to flyout */
.catalog-flyout::before,
.documents-flyout::before {
	content: '';
	position: absolute;
	top: 0;
	left: -10px;
	width: 10px;
	height: 100%;
}

@keyframes catalog-flyout-in {
	from {
		opacity: 0;
		transform: translateX(-4px);
	}
	to {
		opacity: 1;
		transform: translateX(0);
	}
}

.catalog-flyout-item,
.documents-flyout-item {
	width: 100%;
	border: none;
	background: transparent;
	color: var(--text-color-secondary);
	display: flex;
	align-items: center;
	padding: 8px 12px;
	border-radius: 4px;
	font-size: 0.85rem;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.12s ease;
	text-align: left;
	white-space: nowrap;
}

.catalog-flyout-item:hover,
.documents-flyout-item:hover,
.documents-flyout-item:focus-visible {
	color: var(--text-color);
	background: var(--noc-background-secondary);
}

.catalog-flyout-item.active,
.documents-flyout-item.active {
	background: var(--noc-blue-05);
	color: var(--noc-blue);
}

.documents-parent:focus-visible,
.documents-flyout-item:focus-visible {
	outline: 2px solid var(--noc-blue, #0a61c9);
	outline-offset: 1px;
}

.menu-hint {
	margin: 4px 12px 0;
	font-size: 0.8rem;
	color: var(--text-color-secondary);
}

.menu-item {
	width: 100%;
	border: none;
	background: transparent;
	color: var(--text-color-secondary);
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 10px 12px;
	border-radius: 8px;
	font-size: 0.9rem;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.15s ease;
	text-align: left;
}

.summary-due-count {
	margin-left: auto;
}

.menu-item-group {
	display: flex;
	flex-direction: column;
	border: 1px solid transparent;
	border-radius: 8px;
	padding: 0;
	transition: padding 0.16s ease, border-color 0.16s ease;
}

.menu-item-group.has-subitem {
	border-color: var(--border-color);
	padding: 2px;
	/* Items inside keep 8px corners; the frame adds its 2px padding. */
	border-radius: 10px;
}

.menu-subitem-wrap {
	width: 100%;
	margin-top: 2px;
}

.menu-subitem-wrap .menu-subitem {
	margin-top: 0;
}

.menu-subitem-wrap-transition-enter-active,
.menu-subitem-wrap-transition-leave-active {
	transition: opacity 0.16s ease, transform 0.16s ease, max-height 0.16s ease, margin-top 0.16s ease;
	overflow: hidden;
}

.menu-subitem-wrap-transition-enter-from,
.menu-subitem-wrap-transition-leave-to {
	opacity: 0;
	transform: translateY(-4px);
	max-height: 0;
	margin-top: 0;
}

.menu-subitem-wrap-transition-enter-to,
.menu-subitem-wrap-transition-leave-from {
	opacity: 1;
	transform: translateY(0);
	max-height: 52px;
	margin-top: 2px;
}

.menu-item:hover,
/* While its flyout is open, the parent keeps the hover look, even with the pointer on the flyout. */
.menu-item.is-expanded {
	color: var(--text-color);
	/* A shade darker than the grey sidebar. */
	background: color-mix(in srgb, var(--text-color) 5%, var(--noc-sidebar-background));
}

.menu-item.active {
	background: var(--noc-blue-05);
	color: var(--noc-blue);
}

.menu-item:disabled {
	opacity: 1;
}

.menu-footer {
	margin-top: auto;
	display: flex;
	flex: 0 0 auto;
	min-height: 0;
	flex-direction: column;
	justify-content: flex-end;
	gap: 8px;
}

.user-profile-section {
	position: relative;
	z-index: 1;
	padding: 0;
	background-color: transparent;
	border-radius: 0;
	display: flex;
	justify-content: stretch;
}

.user-actions {
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: flex-end;
	gap: 6px;
	width: 100%;
}

.user-action-btn {
	width: 32px;
	height: 32px;
	border: none;
	background: transparent;
	color: var(--text-color);
	opacity: 1;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 8px;
	cursor: pointer;
	transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.menu-back-btn {
	border: none;
	background: transparent;
	color: var(--text-color-secondary);
	height: 32px;
	padding: 0 10px;
	border-radius: 4px;
	font-size: 0.8rem;
	font-weight: 600;
	display: inline-flex;
	align-items: center;
	gap: 6px;
	cursor: pointer;
	transition: all 0.15s ease;
}

.menu-back-btn:hover:not(:disabled) {
	color: var(--noc-blue, #0a61c9);
	background: var(--noc-blue-05);
	border-color: transparent;
}

.menu-back-btn:disabled {
	opacity: 0.45;
	cursor: default;
}

.menu-back-icon {
	width: 0.8rem;
	height: 0.8rem;
}

.user-action-btn:hover {
	color: var(--noc-blue, #0a61c9);
	background: color-mix(in srgb, var(--text-color) 5%, var(--noc-sidebar-background));
	opacity: 1;
}

.user-action-btn.active {
	color: var(--noc-blue, #0a61c9);
	background: #eff4fb;
	border-color: var(--noc-blue-20);
	opacity: 1;
}

.menu-icon {
	font-size: 1.3rem;
	width: 1.3rem;
	height: 1.3rem;
}

.item-icon {
	font-size: 1.15rem;
	width: 1.15rem;
	height: 1.15rem;
	flex-shrink: 0;
}

.dashboard-content {
	flex: 1 1 auto;
	display: flex;
	flex-direction: column;
	min-height: 0;
	min-width: 0;
	overflow: hidden;
}

.tab-content-area {
	flex: 1 1 auto;
	min-height: 0;
}
</style>
